"use client"

import {
  createExpressCart as createExpressCartAction,
  addToExpressCart as addToExpressCartAction,
  updateExpressCart as updateExpressCartAction,
  setExpressShippingMethod,
  initiateExpressPaymentSession,
  completeExpressCart as completeExpressCartAction,
  updateExpressLineItem,
  retrieveExpressCart,
} from "@/lib/data/express-cart"
import { HttpTypes } from "@medusajs/types"
import { toast } from "@medusajs/ui"
import {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react"

type ExpressCartContextType = {
  cart: HttpTypes.StoreCart | null
  isLoading: boolean
  error: string | null
  createExpressCart: (regionId: string) => Promise<HttpTypes.StoreCart | null>
  addToExpressCart: (variantId: string, quantity: number) => Promise<void>
  updateExpressCart: (data: HttpTypes.StoreUpdateCart) => Promise<void>
  setShippingMethod: (shippingMethodId: string) => Promise<void>
  initiatePayment: (providerId: string) => Promise<void>
  completeExpressCart: () => Promise<HttpTypes.StoreOrder | null>
  updateItemQuantity: (lineItemId: string, quantity: number) => Promise<void>
  refreshCart: () => Promise<void>
  refreshCartById: (cartId: string) => Promise<void>
  setCartDirect: (cart: HttpTypes.StoreCart) => void
  unsetCart: () => void
}

const ExpressCartContext = createContext<ExpressCartContextType | undefined>(
  undefined
)

/**
 * ExpressCartProvider manages a dedicated cart for express checkout flow
 * It creates a separate cart from the main shopping cart
 */
export function ExpressCartProvider({
  children,
  initialCart,
}: {
  children: React.ReactNode
  initialCart?: HttpTypes.StoreCart | null
}) {
  const [cart, setCart] = useState<HttpTypes.StoreCart | null>(
    initialCart || null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create a new express cart
  const createExpressCart = useCallback(
    async (regionId: string): Promise<HttpTypes.StoreCart | null> => {
      setIsLoading(true)
      setError(null)

      try {
        const newCart = await createExpressCartAction(regionId)
        setCart(newCart)
        return newCart
      } catch (err: any) {
        const message = err.message || "Napaka pri ustvarjanju košarice"
        setError(message)
        toast.error(message)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  // Add item to express cart
  const addToExpressCart = useCallback(
    async (variantId: string, quantity: number) => {
      if (!cart) {
        setError("Košarica ni bila ustvarjena")
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const updatedCart = await addToExpressCartAction(cart.id, variantId, quantity)
        setCart(updatedCart)
      } catch (err: any) {
        const message = err.message || "Napaka pri dodajanju v košarico"
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    },
    [cart]
  )

  // Update express cart (shipping/billing address, email, etc.)
  const updateExpressCart = useCallback(
    async (data: HttpTypes.StoreUpdateCart) => {
      if (!cart) {
        setError("Košarica ni bila ustvarjena")
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const updatedCart = await updateExpressCartAction(cart.id, data)
        setCart(updatedCart)
      } catch (err: any) {
        const message = err.message || "Napaka pri posodabljanju košarice"
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    },
    [cart]
  )

  // Set shipping method
  const setShippingMethod = useCallback(
    async (shippingMethodId: string) => {
      if (!cart) {
        setError("Košarica ni bila ustvarjena")
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const updatedCart = await setExpressShippingMethod(cart.id, shippingMethodId)
        setCart(updatedCart)
      } catch (err: any) {
        const message = err.message || "Napaka pri izbiri načina dostave"
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    },
    [cart]
  )

  // Initiate payment session
  const initiatePayment = useCallback(
    async (providerId: string) => {
      if (!cart) {
        setError("Košarica ni bila ustvarjena")
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const resp = await initiateExpressPaymentSession(cart, providerId)
        // Update cart with payment collection
        if (resp?.payment_collection) {
          setCart((prev) =>
            prev ? { ...prev, payment_collection: resp.payment_collection } : null
          )
        }
        // Refresh cart to get latest state
        await refreshCartInternal()
      } catch (err: any) {
        const message = err.message || "Napaka pri ustvarjanju plačila"
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    },
    [cart]
  )

  // Internal refresh cart function
  const refreshCartInternal = async () => {
    if (!cart) return
    try {
      const updatedCart = await retrieveExpressCart(cart.id)
      if (updatedCart) {
        setCart(updatedCart)
      }
    } catch {
      // Silently fail - cart state remains unchanged
    }
  }

  // Complete the express cart (place order)
  const completeExpressCart = useCallback(async (): Promise<HttpTypes.StoreOrder | null> => {
    if (!cart) {
      setError("Košarica ni bila ustvarjena")
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await completeExpressCartAction(cart.id)

      if (response.type === "order") {
        setCart(null) // Clear cart after successful order
        return response.order
      } else {
        setError("Napaka pri zaključku naročila")
        toast.error("Napaka pri zaključku naročila")
        return null
      }
    } catch (err: any) {
      const message = err.message || "Napaka pri zaključku naročila"
      setError(message)
      toast.error(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [cart])

  // Update item quantity
  const updateItemQuantity = useCallback(
    async (lineItemId: string, quantity: number) => {
      if (!cart) {
        setError("Košarica ni bila ustvarjena")
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const updatedCart = await updateExpressLineItem(cart.id, lineItemId, quantity)
        setCart(updatedCart)
      } catch (err: any) {
        const message = err.message || "Napaka pri posodabljanju količine"
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    },
    [cart]
  )

  // Refresh cart data
  const refreshCart = useCallback(async () => {
    if (!cart) return

    setIsLoading(true)
    setError(null)

    try {
      const updatedCart = await retrieveExpressCart(cart.id)
      if (updatedCart) {
        setCart(updatedCart)
      }
    } catch (err: any) {
      setError(err.message || "Napaka pri osveževanju košarice")
    } finally {
      setIsLoading(false)
    }
  }, [cart])

  // Unset cart
  const unsetCart = useCallback(() => {
    setCart(null)
    setError(null)
  }, [])

  // Refresh cart by ID (avoids stale closure issues)
  const refreshCartById = useCallback(async (cartId: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const updatedCart = await retrieveExpressCart(cartId)
      if (updatedCart) {
        setCart(updatedCart)
      }
    } catch (err: any) {
      setError(err.message || "Napaka pri osveževanju košarice")
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Set cart state directly (for syncing after direct API calls)
  const setCartDirect = useCallback((newCart: HttpTypes.StoreCart) => {
    setCart(newCart)
  }, [])

  return (
    <ExpressCartContext.Provider
      value={{
        cart,
        isLoading,
        error,
        createExpressCart,
        addToExpressCart,
        updateExpressCart,
        setShippingMethod,
        initiatePayment,
        completeExpressCart,
        updateItemQuantity,
        refreshCart,
        refreshCartById,
        setCartDirect,
        unsetCart,
      }}
    >
      {children}
    </ExpressCartContext.Provider>
  )
}

export function useExpressCart() {
  const context = useContext(ExpressCartContext)
  if (context === undefined) {
    throw new Error("useExpressCart must be used within an ExpressCartProvider")
  }
  return context
}
